import { Button, Space, Typography, Form, InputNumber, Select, Tooltip, Input } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTractor, faPlus, faX, faCircleQuestion } from '@fortawesome/pro-solid-svg-icons'
import { HeavyMachineryStepProps } from '../../types'
import { HEAVY_MACHINERY_OPTIONS, OWNERSHIP_TYPE_OPTIONS, MAX_ENTITIES } from '../../constants'
import { checkEquipmentRow } from '../../inputSanityModel'
import InputWarning from '../InputWarning'

const { Title, Paragraph } = Typography

export const HeavyMachineryStep = ({
  data,
  onCountChange,
  onMachineryUpdate,
  onMachineryAdd,
  onMachineryRemove, crossInputCard }: HeavyMachineryStepProps) => {
  return (
    <Space
      direction="vertical"
      size="large"
      style={{
        width: '100%',
        maxWidth: '1200px',
        padding: '40px 20px'
      }}
    >
      <div style={{
        backgroundColor: 'white',
        padding: '32px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <Space direction="horizontal" size="middle" style={{ marginBottom: '16px' }}>
          <FontAwesomeIcon icon={faTractor} style={{ fontSize: '24px', color: '#ff6b4a' }} />
          <Title level={3} style={{ margin: 0 }}>Heavy Machinery</Title>
        </Space>

        <Paragraph style={{ color: '#666', marginBottom: '32px' }}>
          Add heavy machinery and calculate depreciation costs.
        </Paragraph>

        {crossInputCard}

        <Form layout="vertical">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Form.Item
              label="How many pieces of heavy machinery do you have?"
              required
              style={{ marginBottom: 0 }}
              help="Total number of heavy machinery pieces (excavators, loaders, etc.)"
            >
              <InputNumber
                value={data.machineryCount}
                onChange={(value) => onCountChange(value || 0)}
                min={0}
                max={MAX_ENTITIES}
                style={{ width: '100%' }}
                size="large"
              />
            </Form.Item>

            {data.machinery.map((machinery, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: '8px',
                  padding: '24px',
                  position: 'relative'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <Title level={5} style={{ margin: 0 }}>Heavy Machinery #{index + 1}</Title>
                  <Button
                    type="text"
                    icon={<FontAwesomeIcon icon={faX} />}
                    onClick={() => onMachineryRemove(index)}
                    style={{ color: '#999' }}
                  />
                </div>

                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <Space direction="horizontal" size="large" style={{ width: '100%', alignItems: 'flex-start' }}>
                    <Form.Item
                      label="Equipment Name"
                      required
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Select
                        value={machinery.equipmentName}
                        onChange={(value) => onMachineryUpdate(index, 'equipmentName', value)}
                        size="large"
                        options={HEAVY_MACHINERY_OPTIONS}
                      />
                    </Form.Item>

                    <Form.Item
                      label="Ownership Type"
                      required
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Select
                        value={machinery.ownershipType}
                        onChange={(value) => onMachineryUpdate(index, 'ownershipType', value)}
                        size="large"
                        options={OWNERSHIP_TYPE_OPTIONS}
                      />
                    </Form.Item>
                  </Space>

                  {machinery.equipmentName === 'Other' && (
                    <Form.Item
                      label="Custom Equipment Name"
                      required
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Input
                        value={machinery.customEquipmentName || ''}
                        onChange={(e) => onMachineryUpdate(index, 'customEquipmentName', e.target.value)}
                        placeholder="Enter equipment name"
                        size="large"
                      />
                    </Form.Item>
                  )}

                  {machinery.ownershipType === 'Own' ? (
                    <>
                      <Space direction="horizontal" size="large" style={{ width: '100%', alignItems: 'flex-start' }}>
                        <Form.Item
                          label={
                            <span>
                              Replacement Cost{' '}
                              <Tooltip title="The cost to replace this equipment with a similar vehicle">
                                <FontAwesomeIcon icon={faCircleQuestion} style={{ color: '#999', fontSize: '14px' }} />
                              </Tooltip>
                            </span>
                          }
                          required
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <InputNumber
                            prefix="$"
                            value={machinery.replacementCost}
                            onChange={(value) => onMachineryUpdate(index, 'replacementCost', value)}
                            min={0}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                            style={{ width: '100%' }}
                            size="large"
                            placeholder="45,000"
                          />
                        </Form.Item>

                        <Form.Item
                          label={
                            <span>
                              Avg Useful Life{' '}
                              <Tooltip title="Average number of years this equipment will be in service">
                                <FontAwesomeIcon icon={faCircleQuestion} style={{ color: '#999', fontSize: '14px' }} />
                              </Tooltip>
                            </span>
                          }
                          required
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <InputNumber
                            value={machinery.avgUsefulLife}
                            onChange={(value) => onMachineryUpdate(index, 'avgUsefulLife', value)}
                            min={0}
                            addonAfter="Years"
                            style={{ width: '100%' }}
                            size="large"
                            placeholder="10"
                          />
                        </Form.Item>

                        <Form.Item
                          label={
                            <span>
                              Sell Price{' '}
                              <Tooltip title="Expected resale value at the end of its useful life">
                                <FontAwesomeIcon icon={faCircleQuestion} style={{ color: '#999', fontSize: '14px' }} />
                              </Tooltip>
                            </span>
                          }
                          required
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <InputNumber
                            prefix="$"
                            value={machinery.sellPrice}
                            onChange={(value) => onMachineryUpdate(index, 'sellPrice', value)}
                            min={0}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                            style={{ width: '100%' }}
                            size="large"
                            placeholder="15,000"
                          />
                        </Form.Item>
                      </Space>

                      <InputWarning warning={checkEquipmentRow(machinery)} />
                    </>
                  ) : (
                    <Form.Item
                      label="Monthly Payment"
                      required
                      style={{ flex: 1, marginBottom: 0, maxWidth: '50%' }}
                    >
                      <InputNumber
                        prefix="$"
                        value={machinery.monthlyPayment}
                        onChange={(value) => onMachineryUpdate(index, 'monthlyPayment', value)}
                        min={0}
                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                        style={{ width: '100%' }}
                        size="large"
                        placeholder="1,000"
                      />
                    </Form.Item>
                  )}
                </Space>
              </div>
            ))}

            <Button
              size="large"
              onClick={onMachineryAdd}
              icon={<FontAwesomeIcon icon={faPlus} />}
              style={{ width: '100%' }}
              disabled={data.machinery.length >= MAX_ENTITIES}
            >
              Add Another Heavy Machinery
            </Button>
          </Space>
        </Form>
      </div>
    </Space>
  )
}
